import HttpFactory from '~/repository/factory'
import type {
  StudyCompleteResponse,
  StudyListItem,
  FundCatalogItem,
  FundsCatalogQuery,
  LifeCoverageItem,
  LifeProductDetailResponse,
  SaveSummaryPayload,
  SaveDetailPayload,
  CotacaoPayload,
  CotacaoRapidaPayload,
  CotacaoResponse,
  CoverageRulesResponse,
} from '~/types/study'

class StudyModule extends HttpFactory {
  private RESOURCE = '/study'

  async create(leadId: string, title?: string): Promise<StudyCompleteResponse> {
    return this.post<StudyCompleteResponse>(this.RESOURCE, { lead_id: leadId, title })
  }

  async countByLead(leadId: string): Promise<number> {
    return this.get<number>(`${this.RESOURCE}/count`, { query: { lead_id: leadId } })
  }

  async findByLead(leadId: string): Promise<StudyListItem[]> {
    return this.get<StudyListItem[]>(this.RESOURCE, { query: { lead_id: leadId } })
  }

  async findComplete(id: string): Promise<StudyCompleteResponse> {
    return this.get<StudyCompleteResponse>(`${this.RESOURCE}/${id}`)
  }

  async saveSummary(id: string, payload: SaveSummaryPayload): Promise<StudyCompleteResponse> {
    return this.put<StudyCompleteResponse>(`${this.RESOURCE}/${id}/summary`, payload)
  }

  async saveDetail(id: string, payload: SaveDetailPayload): Promise<StudyCompleteResponse> {
    return this.put<StudyCompleteResponse>(`${this.RESOURCE}/${id}/detail`, payload)
  }

  async patchStatus(id: string, status: 'finalized' | 'archived'): Promise<StudyCompleteResponse> {
    return this.put<StudyCompleteResponse>(`${this.RESOURCE}/${id}/status`, { status })
  }

  async duplicate(id: string, aquarioId?: string): Promise<StudyCompleteResponse> {
    return this.post<StudyCompleteResponse>(
      `${this.RESOURCE}/${id}/duplicate`,
      aquarioId ? { aquario_id: aquarioId } : undefined,
    )
  }

  async remove(id: string): Promise<void> {
    return this.delete(`${this.RESOURCE}/${id}`)
  }

  async getHistory(id: string): Promise<unknown[]> {
    return this.get<unknown[]>(`${this.RESOURCE}/${id}/history`)
  }

  async getCoverages(): Promise<LifeCoverageItem[]> {
    return this.get<LifeCoverageItem[]>(`${this.RESOURCE}/coverages`)
  }

  async getFundsCatalog(params?: FundsCatalogQuery): Promise<FundCatalogItem[]> {
    return this.get<FundCatalogItem[]>(`${this.RESOURCE}/funds/catalog`, { query: params })
  }

  async getLifeProductDetails(productCode: string): Promise<LifeProductDetailResponse | null> {
    return this.get<LifeProductDetailResponse | null>(`${this.RESOURCE}/life-products/${productCode}`)
  }

  async createCotacao(id: string, payload: CotacaoPayload): Promise<CotacaoResponse> {
    return this.post<CotacaoResponse>(`${this.RESOURCE}/${id}/cotacao`, payload)
  }

  // Cotação rápida efêmera (AR-197 / UC087): sem lead/estudo, nada persistido.
  async cotacaoRapida(payload: CotacaoRapidaPayload): Promise<CotacaoResponse> {
    return this.post<CotacaoResponse>(`${this.RESOURCE}/cotacao-rapida`, payload)
  }

  async getCoverageRules(): Promise<CoverageRulesResponse> {
    return this.get<CoverageRulesResponse>(`${this.RESOURCE}/coverage-rules`)
  }

  async downloadPdf(id: string): Promise<Blob> {
    return this.get<Blob>(`${this.RESOURCE}/${id}/pdf`, { responseType: 'blob' })
  }
}

export default StudyModule
